import { useState } from 'react';
import { toast } from 'sonner';
import { useCan } from '@discovr/common';
import { useCampaignCapabilitiesStore, useOutreachHandleImport } from '@discovr/features';
import { SCOPES, type OutreachHandlePreview } from '@discovr/types';
import { canWriteAtLevel } from '../lib/campaignPermissions';
import { useOutreachSubParam } from '../campaign-detail/useOutreachSubParam';
import { POOL_COPY } from './constants/findCopy';
import { errorText } from './discoveryErrors';
import { plural } from './discoveryText';
import { outreachHandles, type PoolCreator } from './poolModel';

const COPY = POOL_COPY.outreach;

export function previewSummary(preview: OutreachHandlePreview): { title: string; body: string } {
  const { counts } = preview;
  const adding = plural(counts.ok, 'creator');
  const cantAdd = counts.invalid + counts.duplicate + counts.hasPrice + counts.wrongPlatform;
  const leftOut = [
    ...(counts.alreadyInCampaign ? [COPY.alreadyIn(counts.alreadyInCampaign)] : []),
    ...(cantAdd ? [COPY.cantAdd(cantAdd)] : []),
  ];
  return { title: COPY.title(adding), body: COPY.body(adding, leftOut) };
}

/** Pool's "Send to outreach": preview the selected handles, confirm, then start the outreach import. */
export function usePoolOutreach(campaignId: string, onSent: () => void) {
  const { preview, commit } = useOutreachHandleImport(campaignId);
  const { setSub } = useOutreachSubParam();
  // Mirrors the Outreach tab's own gate: the role may write outreach, and the campaign grant allows it here.
  const accessLevel = useCampaignCapabilitiesStore((s) => s.accessLevel);
  const canSend = useCan(SCOPES.outreachWrite) && canWriteAtLevel(accessLevel);
  const [previewed, setPreviewed] = useState<OutreachHandlePreview | null>(null);

  const blockedReason = (creators: readonly PoolCreator[]): string | null => {
    if (!canSend) return COPY.noAccess;
    return outreachHandles(creators).length ? null : COPY.instagramOnly;
  };

  const start = (creators: readonly PoolCreator[]) => {
    preview.mutate(outreachHandles(creators), {
      onSuccess: (result) => {
        if (result.counts.ok) setPreviewed(result);
        else toast.info(COPY.nothingNew);
      },
      onError: (error) => toast.error(errorText(error)),
    });
  };

  const confirm = () => {
    if (!previewed) return;
    commit.mutate(previewed, {
      onSuccess: () => {
        toast.success(COPY.sent(plural(previewed.counts.ok, 'creator')), {
          action: { label: COPY.open, onClick: () => setSub('inbox') },
        });
        setPreviewed(null);
        onSent();
      },
      onError: (error) => toast.error(errorText(error)),
    });
  };

  return {
    blockedReason,
    start,
    confirm,
    cancel: () => setPreviewed(null),
    previewed,
    previewing: preview.isPending,
    committing: commit.isPending,
  };
}
